import store from '@/store';
import StorageService from './StorageService';
import * as arobaPublicHttp from './api/aroba-oapi-http.service';
import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  resetPassword as cognitoResetPassword,
  confirmResetPassword,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from 'aws-amplify/auth';
import jwtDecode from 'jwt-decode';

import { HTTPHeaders, HistoryCode } from '@/globals/enums';
import { STORAGE_KEYS } from '@/constants/storage';
import { errorMessages } from '@/globals/errorMessages';
import arobaAxiosInstance from '@/services/axios/aroba-axios.service';

import {
  changeEmail as changeEmailAPI,
  getUserMngId,
  resetMFA,
  resetMFAToken,
} from './user.service';
import { axiosErrorWrapper } from '@/utils/axios.util';
import {
  getCognitoIdToken,
  handleCognitoError,
  isCognitoSessionValid,
} from './cognito.service';
import { logEvent } from './log.service';

export default class AuthService {
  // holds the pending sign-in output during a challenge flow (e.g. NEW_PASSWORD_REQUIRED)
  _pendingSignInOutput = null;

  initLogin() {
    const token = StorageService.getItem(STORAGE_KEYS.SCMCognitoToken);
    if (token) {
      store.dispatch('settings/changeLoggedIn', true);
      store.dispatch('settings/changeImgId', token.img_file_id);
    }
  }

  /**
   * AWS Cognitoの認証を行う
   * @param {String} emailAddress
   * @param {String} password
   * @returns
   */
  async signIn(
    emailAddress,
    password,
    options = { congnitoErrorFn: (error) => handleCognitoError(error) }
  ) {
    try {
      const output = await cognitoSignIn({
        username: emailAddress,
        password,
      });

      const { nextStep } = output;

      if (
        nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        this._pendingSignInOutput = output;
        return { user: output, status: 'NEW_PASSWORD_REQUIRED' };
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE') {
        return { status: 'SMS_MFA', user: output };
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return { status: 'SOFTWARE_TOKEN_MFA', user: output };
      } else if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        await setUpTOTP();
      }

      return { user: output, status: 'NO_ACTION' };
    } catch (err) {
      err.code = err.code || err.name;
      options.congnitoErrorFn(err);
      return { status: 'ERROR', error: err };
    }
  }

  async scmSignIn(user, avatarId) {
    const sessionResult = await isCognitoSessionValid();
    if (!sessionResult.valid) {
      handleCognitoError(sessionResult.error);
      throw sessionResult.error;
    }
    if (user) {
      const userEmail = await this.getCurrentUserEmail();
      const tempCred = await this.getClientTemporaryCredentials(userEmail);
      const { username, password } =
        await this.acceptClientTemporaryCredentials(
          tempCred.tempraryClientCredentials
        );
      const b64Cred = btoa(`${username}:${password}`);
      StorageService.setItem(STORAGE_KEYS.basicAuth, `Basic ${b64Cred}`);
    }
    await this.getCognitoTokenHeaders(avatarId);

    store.dispatch('settings/changeLoggedIn', true);
  }

  /**
   * Sign in with SCM with retry logic for specific error
   * @param {Object} user - Cognito user object
   * @param {number} avatarId - Avatar ID
   * @returns {Promise<void>}
   */
  async scmSignInWithRetry(user, avatarId) {
    let storedAvatarId = avatarId;
    let scmSignInSuccess = false;
    let retryCount = 0;
    const maxRetries = 2;

    while (!scmSignInSuccess && retryCount < maxRetries) {
      try {
        await this.scmSignIn(user, storedAvatarId);
        scmSignInSuccess = true;

        if (retryCount > 0) {
          StorageService.updateRecentAvatars(null);
        }
      } catch (error) {
        if (
          error.status === 400 &&
          error.errorCode === 11004 &&
          retryCount < maxRetries - 1
        ) {
          storedAvatarId = null;
          retryCount++;
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Signout cognito
   */
  signOut = async () => {
    try {
      await cognitoSignOut();
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  };

  /**
   * Get current user information.
   * Returns { id, username, attributes } to preserve v5-compatible shape, or null when unauthenticated.
   * v5 Auth.currentUserInfo() returned null for unauthenticated users; v6 throws instead.
   */
  async getCurrentUserInfo() {
    try {
      const { userId, username } = await getCurrentUser();
      const attrs = await fetchUserAttributes();
      return { id: userId, username, attributes: attrs };
    } catch (error) {
      if (error.name === 'UserUnAuthenticatedException') {
        return null;
      }
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  /**
   * Returns the stored pending sign-in output during a challenge flow.
   */
  async getCurrentUnAuthenticatedUser() {
    try {
      return this._pendingSignInOutput;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   */
  async currentAuthenticatedUser() {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  // Get the idToken
  async getIdToken() {
    try {
      return getCognitoIdToken();
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async getIamAccessKey(cameraIds) {
    const payload = {};
    if (cameraIds) {
      payload.cameraIds = cameraIds;
      payload.isFeature = true;
    }
    const idToken = await this.getIdToken();

    try {
      const url =
        process.env.VUE_APP_API_BASE_URL + '/ccs/v1/user/iamAccessKey';

      const res = await arobaAxiosInstance.post(url, payload, {
        headers: {
          [HTTPHeaders.XAuthorization]: idToken,
        },
      });
      const iamRes = res.data;
      store.dispatch('common/changeIam', iamRes);
      return iamRes;
    } catch (error) {
      axiosErrorWrapper(error);
      throw new Error(errorMessages.API_ERROR.error.message);
    }
  }

  async getAuthHeaders() {
    const idToken = await this.getIdToken();
    const token = StorageService.getItem(STORAGE_KEYS.SCMCognitoToken);
    return {
      [HTTPHeaders.XAuthorization]: 'Bearer ' + idToken,
      [HTTPHeaders.Authorization]: StorageService.getItem(
        STORAGE_KEYS.basicAuth
      ),
      [HTTPHeaders.XArobaViewUserToken]: token.accessToken,
    };
  }

  /**
   * Get current cognito session.
   * Returns a shim with a v5-compatible getIdToken() interface for legacy callers.
   */
  getCurrentCognitoSession() {
    return fetchAuthSession().then((session) => ({
      getIdToken: () => ({
        getExpiration: () => session.tokens?.idToken?.payload?.exp ?? 0,
        getJwtToken: () => session.tokens?.idToken?.toString() ?? null,
      }),
    }));
  }

  async forgotPassword(username) {
    try {
      const result = await cognitoResetPassword({ username });
      return result;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async submitForgotPassword(username, code, newPassword) {
    try {
      const result = await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      return result;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  resetPassword() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ status: 200 });
      }, 2000);
    });
  }

  async logout(options = {}) {
    const { skipLogging = false } = options;
    if (!skipLogging) {
      try {
        await logEvent(HistoryCode.Logout);
      } catch (error) {
        console.error('Failed to log logout event:', error);
      }
    }
    await this.signOut();
    store.dispatch('auth/logout', false);
  }

  /**
   * Get totp code
   * @returns
   */
  async getTOTPCode(email) {
    try {
      const totpSetup = await setUpTOTP();
      const code = totpSetup.sharedSecret;
      const str =
        `otpauth://totp/SECOM%20Sights:${email}` +
        '?secret=' +
        code +
        '&issuer=' +
        'SECOM%20Sights';
      return { str, code };
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  /**
   * Verify TOTP code
   * @param {string} code
   * @returns
   */
  async verifyTOTP(code) {
    try {
      const result = await verifyTOTPSetup({ code });
      await this.setMFA('TOTP');
      return result;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async setMFA(mfaType) {
    try {
      let preference = {};
      if (mfaType === 'TOTP') {
        preference = { totp: 'PREFERRED' };
      } else if (mfaType === 'SMS') {
        preference = { sms: 'PREFERRED' };
      } else {
        preference = { totp: 'DISABLED', sms: 'DISABLED' };
      }
      await updateMFAPreference(preference);
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async setUserPhone(user, phone) {
    try {
      await updateUserAttributes({
        userAttributes: { phone_number: phone },
      });
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async updateUserPhone(newPhoneNumber) {
    try {
      await updateUserAttributes({
        userAttributes: { phone_number: newPhoneNumber },
      });
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async changeEmail(oldEmail, newEmail) {
    const { user_mng_id } = await getUserMngId(encodeURIComponent(oldEmail));
    await changeEmailAPI(user_mng_id, {
      mail_address: newEmail,
    });
  }

  resetUserMFA(username) {
    return resetMFA({
      username,
    });
  }

  resetUserMFAByToken(token) {
    return resetMFAToken({
      token,
    });
  }

  async getUserPhone() {
    try {
      const attrs = await fetchUserAttributes();
      return attrs.phone_number || null;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async sendPhoneVerifyCode() {
    try {
      const result = await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
      return result;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  async veifyPhoneCode(code) {
    try {
      const result = await confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: code,
      });
      return result;
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  }

  completeNewPassword = async (user, newPassword) => {
    try {
      // user param not used in v6; confirmSignIn relies on internal sign-in state
      const output = await confirmSignIn({ challengeResponse: newPassword });
      this._pendingSignInOutput = null;
      return { user: output, status: 'COMPLETE_NEW_PASSWORD' };
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      return { status: 'ERROR', error };
    }
  };

  /**
   * Confirm sign in with TOTP/SMS code
   */
  async confirmSignInCode(_user, code, _mfaType) {
    let result = null;
    try {
      // _user and _mfaType params not needed in v6; confirmSignIn uses internal state
      result = await confirmSignIn({ challengeResponse: code });
    } catch (error) {
      error.code = error.code || error.name;
      if (error.code === 'NotAuthorizedException') {
        error.code = 'NotAuthorizedException.AuthCode';
      }
      handleCognitoError(error);
      throw error;
    }
    if (result?.isSignedIn) {
      await this.scmSignIn(result);
    }
  }

  async getCurrentUserEmail() {
    const idToken = await this.getIdToken();
    return jwtDecode(idToken).email;
  }

  /**
   * Confirm client temporary credentials
   */
  async acceptClientTemporaryCredentials(cred) {
    const payload = {
      tempraryClientCredentials: cred,
      appType: 1,
    };
    const result = await arobaPublicHttp.post(
      `${process.env.VUE_APP_API_BASE_URL}/ccs/v1/user/client/credentials/accept`,
      payload
    );
    return result;
  }

  /**
   * Get client temporary credentials
   */
  async getClientTemporaryCredentials(email) {
    const clientUuid = self.crypto.randomUUID();
    const payload = {
      appType: 1,
      clientUuid,
      userAccessKey: email,
    };
    const result = await arobaPublicHttp.post(
      `${process.env.VUE_APP_API_BASE_URL}/ccs/v1/user/client/credentials/temporary`,
      payload
    );
    return result;
  }

  /**
   * Get cognito token headers
   */
  async getCognitoTokenHeaders(avatarId) {
    const idToken = await this.getIdToken();
    const payload = {
      userAccessKey: jwtDecode(idToken).email,
      providerType: 1,
    };
    if (avatarId) {
      payload.avatarId = avatarId;
    }

    Object.setPrototypeOf(payload, {
      __headers: {
        [HTTPHeaders.XAuthorization]: idToken,
        [HTTPHeaders.Authorization]: StorageService.getItem(
          STORAGE_KEYS.basicAuth
        ),
      },
    });
    const result = await arobaPublicHttp.post(
      `${process.env.VUE_APP_API_BASE_URL}/ccs/v1/user/cognitoAuth`,
      payload,
      (err) => !(err.status === 400 && err.errorCode === 11004)
    );
    StorageService.setItem(STORAGE_KEYS.SCMCognitoToken, result);
    return result;
  }

  /**
   * Get current user MFA setting.
   * Returns v5-compatible strings: 'SOFTWARE_TOKEN_MFA', 'SMS_MFA', 'NOMFA'
   */
  getCurrentUserMFA = async () => {
    try {
      const mfaPreference = await fetchMFAPreference();
      const preferredMap = {
        TOTP: 'SOFTWARE_TOKEN_MFA',
        SMS: 'SMS_MFA',
      };
      return preferredMap[mfaPreference.preferred] || 'NOMFA';
    } catch (error) {
      error.code = error.code || error.name;
      handleCognitoError(error);
      throw error;
    }
  };
}

export const authService = new AuthService();
